<template>
  <div :class="emptyClassName">
    <woot-loading-state
      v-if="uiFlags.isFetching || loadingChatList"
      :message="loadingIndicatorMessage"
    />
    <!-- No inboxes attached -->
    <div
      v-if="!inboxesList.length && !uiFlags.isFetching && !loadingChatList"
      class="clearfix"
    >
      <onboarding-view v-if="isAdmin" />
      <div v-else class="current-chat">
        <div>
          <img src="~dashboard/assets/images/inboxes.svg" alt="No Inboxes" />
          <span>
            {{ $t('CONVERSATION.NO_INBOX_AGENT') }}
          </span>
          <woot-button
            v-if="showBtnLogout"
            variant="clear"
            color-scheme="secondary"
            size="small"
            icon="power"
            @click="logout"
          >
            {{ $t('SIDEBAR_ITEMS.LOGOUT') }}
          </woot-button>
        </div>
      </div>
    </div>
    <!-- Show empty state images if not loading -->
    <div
      v-else-if="!uiFlags.isFetching && !loadingChatList"
      class="current-chat"
    >
      <!-- No conversations available -->
      <div v-if="!allConversations.length">
        <img src="~dashboard/assets/images/chat-1.svg" alt="No Chat" />
        <span>
          {{ $t('CONVERSATION.NO_MESSAGE_1') }}
          <br />
        </span>
      </div>

      <div
        v-else-if="allConversations.length && !currentChat.id"
        class="attention-state"
      >
        <template v-if="queueCount > 0 || priorityCount > 0">
          <h3>
            <strong>{{ $t('CONVERSATION.ATTENTION_TITLE') }}</strong>
          </h3>

          <div class="attention-content">
            <img
              src="~dashboard/assets/images/chat-1.svg"
              alt="Chat Illustration"
            />

            <div class="attention-text">
              <ul>
                <li>
                  -
                  <strong>
                    {{
                      $t('CONVERSATION.QUEUE_ALERT_DASHBOARD', {
                        count: queueCount,
                      })
                    }}
                  </strong>
                </li>
                <li>
                  -
                  <strong>
                    {{
                      $t('CONVERSATION.PRIORITY_ALERT', {
                        count: priorityCount,
                      })
                    }}
                  </strong>
                </li>
              </ul>
            </div>
          </div>

          <p class="attention-footer">
            {{ $t('CONVERSATION.ATTENTION_FOOTER') }}
          </p>
        </template>

        <template v-else>
          <img src="~dashboard/assets/images/chat-1.svg" alt="No Chat" />
          <span class="conversation-hint">
            {{ conversationMissingMessage }}
          </span>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import Auth from '../../../api/auth';
import { mapGetters } from 'vuex';
import adminMixin from '../../../mixins/isAdmin';
import accountMixin from '../../../mixins/account';
import OnboardingView from './OnboardingView';

export default {
  components: {
    OnboardingView,
  },
  mixins: [accountMixin, adminMixin],
  props: {
    isOnExpandedLayout: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapGetters({
      currentRole: 'getCurrentRole',
      currentChat: 'getSelectedChat',
      allConversations: 'getAllConversations',
      inboxesList: 'inboxes/getInboxes',
      uiFlags: 'inboxes/getUIFlags',
      loadingChatList: 'getChatListLoadingStatus',
      isEmbedding: 'isEmbedding',
      conversationStats: 'conversationStats/getStats',
    }),
    loadingIndicatorMessage() {
      if (this.uiFlags.isFetching) {
        return this.$t('CONVERSATION.LOADING_INBOXES');
      }
      return this.$t('CONVERSATION.LOADING_CONVERSATIONS');
    },
    conversationMissingMessage() {
      if (!this.isOnExpandedLayout) {
        return this.$t('CONVERSATION.SELECT_A_CONVERSATION');
      }
      return this.$t('CONVERSATION.404');
    },
    queueCount() {
      return this.conversationStats?.unAssignedCount || 0;
    },
    priorityCount() {
      return this.conversationStats?.priorityCount || 0;
    },
    emptyClassName() {
      if (
        !this.inboxesList.length &&
        !this.uiFlags.isFetching &&
        !this.loadingChatList &&
        this.isAdmin
      ) {
        return 'inbox-empty-state';
      }
      return 'columns conv-empty-state';
    },
    showBtnLogout() {
      return this.currentRole === 'embed' || this.isEmbedding;
    },
  },
  methods: {
    logout() {
      Auth.logout();
    },
  },
};
</script>

<style lang="scss" scoped>
.inbox-empty-state {
  height: 100%;
  overflow: auto;
}

.current-chat {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
}

.attention-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  text-align: center;

  h3 {
    color: var(--c-500);
    font-size: var(--font-size-large);
    margin-bottom: var(--space-medium);
    font-weight: var(--font-weight-bold);
  }

  .attention-content {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: var(--space-large);

    img {
      width: 18rem;
      max-width: 100%;
    }

    .attention-text {
      text-align: left;

      ul {
        list-style: none;
        padding: 0;
        margin: 0;

        li {
          color: var(--c-500);
          font-size: var(--font-size-default);
          margin: 0.4rem 0;
          font-weight: var(--font-weight-semibold);
        }
      }
    }
  }

  .attention-footer {
    margin-top: var(--space-large);
    font-size: var(--font-size-default);
    color: var(--c-500);
    font-weight: var(--font-weight-medium);
  }

  .conversation-hint {
    color: var(--c-500);
    font-size: var(--font-size-default);
    font-weight: var(--font-weight-semibold);
    margin-top: var(--space-small);
    text-align: center;
    display: block;
  }
}

.conv-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
}
</style>
